import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ChatHeader from "../../components/chat-header";
import { useLayoutContext } from "../../layouts/general-layout";
import { RouterConstant } from "../../routers/index";
import Button from "../../components/button";
import { SearchExerciseService } from "../../services/search-exercise/search-exercise.service";
import { SelfTestService } from "../../services/self-test/self-test-service";
import "./self-test-exercises.scss";
import TestExerciseCard from "../../components/test-exercise-card";
import { useSelfTestStore } from "../../utils/selfTestStore";


export default function Index() {
    const { t } = useLayoutContext();
    const navigate = useNavigate();
    const { ROUTERS_PATH } = RouterConstant;
    const { state } = useLocation();
    const { selectedLevelId, selectedTopicId } = state;

    const searchExerciseService = new SearchExerciseService();
    const selfTestService = new SelfTestService();

    const saveSelectedAnswer = useSelfTestStore((s) => s.saveSelectedAnswer);
    const getStoredAnswer = useSelfTestStore((s) => s.getSelectedAnswer);
    const setSelectedTestId = useSelfTestStore((s) => s.setSelectedTestId);
    const selected_test_id = useSelfTestStore((s) => s.selected_test_id);

    const [isExerciseLoading, setIsExerciseLoading] = useState(false);
    const [isAnswersLoading, setIsAnswersLoading] = useState(false);
    const [showFullExercise, setShowFullExercise] = useState(false);
    const [isSubmitingAnswers, setIsSubmitingAnswers] = useState(false);

    const [exerciseIds, setExerciseIds] = useState<number[]>([]);
    const [testId, setTestId] = useState<number | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [exercise, setExercise] = useState<any>(null);
    const [answers, setAnswers] = useState<any[]>([]);
    const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

    useEffect(() => {
        if (selectedLevelId && selectedTopicId) {
            fetchTestData();
        }
    }, [selectedLevelId, selectedTopicId]);

    const fetchTestData = async () => {
        setIsExerciseLoading(true);
        setIsAnswersLoading(true);
    
        try {
            const response = await selfTestService.get_self_test_exercise_ids({
                topic_id: Number(selectedTopicId),
                level_id: Number(selectedLevelId),
            });
    
            const tid = response.test_info?.test_id || null;
            setTestId(tid);
            setSelectedTestId(tid);
    
            if (response.exercise_ids?.length > 0) {
                setExerciseIds(response.exercise_ids);
                setCurrentIndex(0);
                await fetchExercise(response.exercise_ids[0]);
            } else {
                setExerciseIds([]);
            }
        } catch (err) {
            console.error("Failed to load test data", err);
        }
    };
    

    const fetchExercise = async (exercise_id: number) => {
        setIsExerciseLoading(true);
        setIsAnswersLoading(true);
    
        setExercise(null);
        setAnswers([]);
        setSelectedAnswer(null);
    
        try {
            const data = await searchExerciseService.getLiteExerciseContent(exercise_id);
            setExercise(data);
            const stored = getStoredAnswer(exercise_id);
            
            if (stored && stored.selected_answer !== undefined) {
                setSelectedAnswer(stored.selected_answer);
            }
        } catch (error) {
            console.error("Failed to load exercise", error);
        }
    
        try {
            const ans = await selfTestService.getExercise_test_answers(exercise_id);
            setAnswers(ans?.exercise_answers || []);
        } catch (error) {
            console.error("Failed to load answers", error);
        }
    
        setIsAnswersLoading(false);
        setIsExerciseLoading(false);
    };
    

    const handleAnswerSelect = async (answer_id: number) => {
        setSelectedAnswer(answer_id);
        saveSelectedAnswer(exercise.id, answer_id);
    };

    const handleNext = async () => {
        if (selectedAnswer === null || selectedAnswer === undefined) {
            saveSelectedAnswer(exercise.id, -1);
        }
        const nextIndex = currentIndex + 1;

        if (nextIndex < exerciseIds.length) {
            setCurrentIndex(nextIndex);
            fetchExercise(exerciseIds[nextIndex]);
            return;
        }

        navigate(ROUTERS_PATH.SELFTESTSUMMARY, {
            state: { testId: selected_test_id, total: exerciseIds.length }
        });
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            const prevIndex = currentIndex - 1;
            setCurrentIndex(prevIndex);
            fetchExercise(exerciseIds[prevIndex]);
            setSelectedAnswer(null);
            setShowFullExercise(false);
        }
    };

    return (
        <div className="self-test-exercise-page">
            <ChatHeader
                chatHeading={t("testYourself")}
                onBackClick={() => navigate(ROUTERS_PATH.SELFTESTSTART)}
                currentIndex={exerciseIds.length ? currentIndex + 1 : 0}
                totalExercises={exerciseIds.length}
            />
            <div className="self-test-exercise-content">
                <TestExerciseCard
                    t={t}
                    exercise={exercise}
                    answers={answers}
                    isExerciseLoading={isExerciseLoading}
                    isAnswersLoading={isAnswersLoading}
                    selectedAnswer={selectedAnswer}
                    showFullExercise={showFullExercise}
                    onAnswerSelect={handleAnswerSelect}
                    onToggleContent={() => setShowFullExercise((prev) => !prev)}
                    isApprovalStatusVisible={false}
                />

                {!isExerciseLoading && (
                    <div className="self-test-exercise-button">
                        <Button type="button" text={t("prevExercise")} onClick={handlePrevious} disabled={currentIndex === 0} outline={true} />
                        <Button type="button" text={t("nextExercise")} color="blue" onClick={handleNext} loader={isSubmitingAnswers}/>
                    </div>
                )}
            </div>
        </div>
    );
}
